import React, { createContext, useState, useContext, ReactNode, useEffect, useCallback, useRef } from 'react';
import { User } from '../types/multiTenant';
import { saveToSupabase, loadFromSupabase, checkSupabaseConnection, supabaseService } from '../services/supabaseService';
import { authService, AuthResult } from '../services/authService';
import userService from '../services/userService';
import { env } from '../config/environment';
import { safeGetItem, safeSetItem, safeRemoveItem } from '../utils/safeStorage';

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  legacyUserId: string | null; // For backwards compatibility with old system
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  syncData: () => Promise<boolean>;
  isSyncing: boolean;
  lastSyncTime: number | null;
  syncError: string | null;
  organizationId: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [legacyUserId, setLegacyUserId] = useState<string | null>(null);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<number | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const syncIntervalRef = useRef<number | null>(null);

  // Função para sincronizar dados (legacy - usa o old user_data table)
  const syncData = useCallback(async (): Promise<boolean> => {
    if (!legacyUserId) return false;

    try {
      setIsSyncing(true);
      setSyncError(null);

      // Verificar conexão
      const isConnected = await checkSupabaseConnection();
      if (!isConnected) {
        setSyncError('Não foi possível conectar ao Supabase. Seus dados estão sendo salvos localmente.');
        setIsSyncing(false);
        return false;
      }

      // Primeiro carregamos os dados do Supabase (caso haja atualizações lá)
      const loadSuccess = await loadFromSupabase(legacyUserId);
      if (!loadSuccess) {
        setSyncError('Erro ao carregar dados do Supabase');
        setIsSyncing(false);
        return false;
      }

      // Depois salvamos os dados locais no Supabase
      const saveSuccess = await saveToSupabase(legacyUserId);
      if (!saveSuccess) {
        setSyncError('Erro ao salvar dados no Supabase');
        setIsSyncing(false);
        return false;
      }

      // Atualizar timestamp de sincronização
      const now = Date.now();
      setLastSyncTime(now);
      supabaseService.setLastSyncTime(now.toString());
      setIsSyncing(false);
      return true;
    } catch (error) {
      console.error('Erro na sincronização:', error);
      setIsSyncing(false);
      setSyncError('Erro ao sincronizar: ' + (error instanceof Error ? error.message : 'Erro desconhecido'));
      return false;
    }
  }, [legacyUserId]);

  // Iniciar sincronização periódica quando autenticado - COMENTADO TEMPORARIAMENTE PARA DEBUG
  /*
  useEffect(() => {
    if (isAuthenticated && user) {
      // NÃO sincronizar imediatamente ao autenticar para preservar dados locais
      // A sincronização será feita apenas quando necessário (login, manual, etc.)
      
      // Configurar sincronização em tempo real
      const cleanupRealtime = setupRealtimeSync(user.id);
      
      // Configurar sincronização automática a cada 5 minutos
      const SYNC_INTERVAL = 5 * 60 * 1000; // 5 minutos em milissegundos
      
      syncIntervalRef.current = window.setInterval(() => {
        console.log('Iniciando sincronização automática...');
        syncData();
      }, SYNC_INTERVAL);
      
      // Adicionar listeners para detectar estado online/offline
      const handleOnline = () => {
        console.log('Conexão online detectada. Iniciando sincronização...');
        setSyncError(null);
        syncData();
      };
      
      const handleOffline = () => {
        console.log('Conexão offline detectada.');
        setSyncError('Dispositivo está offline. Seus dados estão sendo salvos localmente.');
      };
      
      try {
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);
    } catch (error) {
      console.warn('Erro ao configurar listeners de rede:', error);
    }
      
      return () => {
        // Limpar intervalo ao desmontar
        if (syncIntervalRef.current !== null) {
          window.clearInterval(syncIntervalRef.current);
          syncIntervalRef.current = null;
        }
        
        // Remover event listeners e listeners de tempo real
        try {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    } catch (error) {
      console.warn('Erro ao remover listeners de rede:', error);
    }
        cleanupRealtime();
      };
    }
  }, [isAuthenticated, user, syncData]);
  */

  // Verificar se há sessão salva
  useEffect(() => {
    const savedUser = safeGetItem('user');
    const savedLegacyUserId = safeGetItem('legacyUserId');
    const savedOrgId = safeGetItem('organizationId');

    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        setUser(userData);
        setIsAuthenticated(true);

        // Recuperar a hora da última sincronização
        const lastSync = supabaseService.getLastSyncTime();
        if (lastSync) {
          setLastSyncTime(parseInt(lastSync, 10));
        }
      } catch (error) {
        console.error('Erro ao recuperar usuário:', error);
      }
    }

    if (savedLegacyUserId) {
      setLegacyUserId(savedLegacyUserId);
    }

    if (savedOrgId) {
      setOrganizationId(savedOrgId);
    }

    setIsLoading(false);
  }, []);

  const login = async (usernameOrEmail: string, password: string): Promise<boolean> => {
    try {
      // Normalize: if it contains @, it's an email - extract username
      // Otherwise, it's a username - construct email
      const isEmail = usernameOrEmail.includes('@');
      const email = isEmail ? usernameOrEmail.toLowerCase() : `${usernameOrEmail.toLowerCase()}@psicosys.com`;

      // First, verify user exists in Supabase database
      const multiTenantUser = await userService.getUserByEmail(email);

      if (!multiTenantUser) {
        if (env.features.enableDebugMode) {
          console.error('User not found in database');
        }
        return false;
      }

      // Check if user is active
      if (!multiTenantUser.isActive) {
        if (env.features.enableDebugMode) {
          console.error('User is inactive');
        }
        return false;
      }

      // Verify password using authService (checks local stored credentials)
      const authResult: AuthResult = await authService.authenticateUser(
        usernameOrEmail.toLowerCase(),
        password
      );

      // If local auth fails, try to verify using stored password hash
      // For now, we'll create local credentials on first successful Supabase lookup
      if (!authResult.success) {
        // Create local credentials for this Supabase user if they don't exist
        const createResult = await authService.createUser(email, password);
        if (!createResult.success) {
          // If creation failed because user exists, try auth again
          const retryAuth = await authService.authenticateUser(email, password);
          if (!retryAuth.success) {
            if (env.features.enableDebugMode) {
              console.error('Password verification failed:', retryAuth.error);
            }
            return false;
          }
        }
      }

      // Authentication successful - set user state
      setLegacyUserId(multiTenantUser.id);
      safeSetItem('legacyUserId', multiTenantUser.id);

      setUser(multiTenantUser);
      setOrganizationId(multiTenantUser.organizationId);
      setIsAuthenticated(true);
      safeSetItem('user', JSON.stringify(multiTenantUser));
      safeSetItem('organizationId', multiTenantUser.organizationId);

      // Update last login
      await userService.updateLastLogin(multiTenantUser.id);

      if (env.features.enableDebugMode) {
        console.log('Login successful - User authenticated');
      }

      return true;
    } catch (error) {
      if (env.features.enableDebugMode) {
        console.error('Erro durante o login:', error);
      }
      return false;
    }
  };

  const logout = async () => {
    // Sincronizar antes de fazer logout (legacy)
    if (legacyUserId) {
      try {
        await saveToSupabase(legacyUserId);
      } catch (error) {
        console.error('Erro ao sincronizar dados antes do logout:', error);
      }

      // Limpar intervalo de sincronização
      if (syncIntervalRef.current !== null) {
        window.clearInterval(syncIntervalRef.current);
        syncIntervalRef.current = null;
      }
    }

    setUser(null);
    setLegacyUserId(null);
    setOrganizationId(null);
    setIsAuthenticated(false);
    safeRemoveItem('user');
    safeRemoveItem('legacyUserId');
    safeRemoveItem('organizationId');
  };

  // Não renderizar os filhos até que a verificação inicial seja concluída
  if (isLoading) {
    return null;
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        legacyUserId,
        organizationId,
        login,
        logout,
        syncData,
        isSyncing,
        lastSyncTime,
        syncError
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};